import DashboardLayout from "components/layouts/dashboard-layout";
import PageLinks from "components/page-links";
import ProgressSteps from "components/progress-steps";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import StepOne from "./components/steps/step1";
import StepsNav from "components/steps-nav";
import { IVideoData, VideoDataKeys, VideoDetailsKeys } from "types/videoData";
import StepTwo from "./components/steps/step2";
import StepThree from "./components/steps/step3";
import StepFour from "./components/steps/step4";

import FinishModal from "./components/finishModal";

const UploadVideo = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [finish, setFinish] = useState(false);
  const [step2CheckList, setStep2CheckList] = useState(false);

  const initialData: IVideoData = {
    releaseTitle: "",
    recordLabel: "Rated Afrika",
    primaryArtist: "",
    releaseLanguage: "",
    primaryGenre: "",
    secondaryGenre: "",
    videoDetails: { file: undefined, lyricType: "explicit" },
    schedule: { proposedReleaseDate: null, originalReleaseDate: null },
    distribution: {
      stores: [
        { name: "vevo", link: "" },
        { name: "apple", link: "" },
        { name: "tidal", link: "" }
      ]
    }
  };

  const [videoData, setVideoData] = useState<IVideoData>({ ...initialData });

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    } else {
      setFinish(true);
    }
  };

  const handleReleaseDetails = (key: VideoDataKeys, value: string) => {
    setVideoData((prev) => ({ ...prev, [key]: value }));
  };

  const handleVideoDetails = (key: VideoDetailsKeys, value: any) => {
    setVideoData((prev) => ({
      ...prev,
      videoDetails: { ...prev.videoDetails, [key]: value }
    }));
  };

  const getNavigationState = () => {
    let canNext = false;
    let canPrevious = currentStep > 1;

    if (currentStep === 1) {
      canNext =
        !!videoData.primaryArtist &&
        !!videoData.primaryGenre &&
        !!videoData.recordLabel &&
        !!videoData.releaseLanguage &&
        !!videoData.secondaryGenre &&
        !!videoData.releaseTitle;
    }

    if (currentStep === 2) {
      canNext =
        !!videoData.videoDetails.file &&
        step2CheckList &&
        !!videoData.videoDetails.trackArtists;
    }
    if (currentStep === 3) {
      canNext = !!videoData.schedule.proposedReleaseDate;
    }

    if (currentStep === 4) {
      canNext = videoData.distribution.stores.length > 0;
    }

    return { canNext, canPrevious };
  };

  const { canNext, canPrevious } = useMemo(getNavigationState, [
    currentStep,
    videoData,
    step2CheckList
  ]);

  const reset = () => {
    setVideoData({ ...initialData });
    setCurrentStep(1);
    setFinish(false);
    setStep2CheckList(false);
  };

  return (
    <DashboardLayout>
      <>
        <PageLinks
          from={{ name: "Overview", link: "/overview" }}
          current="Upload Video"
        />
        <ProgressSteps
          currentStep={currentStep}
          steps={[
            "Release Details",
            "Video Details",
            "Schedule",
            "Distribution"
          ]}
        />

        <Body>
          {currentStep === 1 ? (
            <StepOne
              videoDetails={videoData}
              onChange={(key, value) => handleReleaseDetails(key, value)}
            />
          ) : currentStep === 2 ? (
            <StepTwo
              videoDetails={videoData.videoDetails}
              onChange={(key, value) => handleVideoDetails(key, value)}
              checkList={step2CheckList}
              setCheckList={(value) => setStep2CheckList(value)}
            />
          ) : currentStep === 3 ? (
            <StepThree
              schedule={videoData.schedule}
              onChange={(schedule) => {
                setVideoData((prev) => ({ ...prev, schedule }));
              }}
            />
          ) : currentStep === 4 ? (
            <StepFour
              distribution={videoData.distribution}
              onChange={(distribution) => {
                setVideoData((prev) => ({ ...prev, distribution }));
              }}
            />
          ) : null}
          {finish && (
            <FinishModal
              active={finish}
              close={() => setFinish(false)}
              videoData={videoData}
              reset={() => {
                reset();
              }}
            />
          )}
        </Body>

        {currentStep > 0 && (
          <StepsNav
            steps={4}
            currentStep={currentStep}
            canNext={canNext}
            canPrevious={canPrevious}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        )}
      </>
    </DashboardLayout>
  );
};

export default UploadVideo;

const Body = styled.div`
  width: 100%;
  padding-top: 2.4rem;
  padding-bottom: 6.4rem;
  min-height: 67vh;
`;
